import { Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StrategyService } from '../../data/services/view/strategy.service';
import { ApiStrategiesService } from '../../data/services/api/api-strategies.service';
import { MenuItem, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';

/**
 * Wizard de creación/edición de estrategias de dripping
 * ACTUALIZADO: 2 pasos (eliminado step3-advanced)
 */
@Component({
  selector: 'app-strategy-wizard',
  templateUrl: './strategy-wizard.component.html',
  styleUrls: ['./strategy-wizard.component.scss'],
  providers: [MessageService]
})
export class StrategyWizardComponent implements OnInit, OnDestroy {
  @Output() onSave = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  
  steps: MenuItem[] = [];
  isLoading = false;
  strategyId: string | null = null;
  private destroy$ = new Subject<void>();
  private initialCheckDone = false;

  constructor(
    public strategyService: StrategyService,
    private route: ActivatedRoute,
    private router: Router,
    private apiStrategiesService: ApiStrategiesService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    // Solo 2 pasos ahora
    this.steps = [
      { label: 'Información Básica', icon: 'pi pi-info-circle' },
      { label: 'Configuración de Canales', icon: 'pi pi-send' }
    ];

    // Verificación SÍNCRONA primero (para modo modal)
    const currentRouteId = this.route.snapshot.paramMap.get('id');
    
    if (currentRouteId) {
      // Modo edición por ruta - cargar estrategia existente
      this.strategyId = currentRouteId;
      this.loadStrategy(currentRouteId);
    } else if (this.strategyService.entity) {
      // Modo modal - formulario YA está cargado desde el padre
      // No hacer nada, dejar que se muestre inmediatamente
      this.initialCheckDone = true;
    } else {
      // No hay formulario - crear uno nuevo
      this.strategyService.newEntity();
      this.initialCheckDone = true;
    }

    // Suscripción a cambios de ruta (solo para navegación dinámica)
    // Esto no afecta el modo modal ya que initialCheckDone = true
    if (!this.initialCheckDone) {
      this.route.paramMap
        .pipe(takeUntil(this.destroy$))
        .subscribe(params => {
          const id = params.get('id');
          if (id && id !== this.strategyId) {
            this.strategyId = id;
            this.loadStrategy(id);
          }
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Carga una estrategia existente desde el API
   */
  loadStrategy(id: string): void {
    this.isLoading = true;
    
    this.apiStrategiesService.getCollection(id).subscribe({
      next: (response: any) => {
        if (!response.error && response.data) {
          // Unificar extracción de datos (puede venir paginado o directo)
          const strategy = response.data?.data || response.data;
          this.strategyService.loadEntityForEdit(strategy);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'No se pudo cargar la estrategia'
          });
          // Redirigir a la lista si no se puede cargar
          this.router.navigate(['/private/dripping/strategies']);
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar la estrategia desde el servidor'
        });
        this.isLoading = false;
        // Redirigir a la lista si hay error
        this.router.navigate(['/private/dripping/strategies']);
      }
    });
  }

  get currentStep() {
    return this.strategyService.currentStep;
  }

  get canGoNext(): boolean {
    return this.strategyService.validateStep(this.currentStep);
  }

  get isLastStep(): boolean {
    return this.currentStep === this.strategyService.maxSteps;
  }

  get canSave(): boolean {
    // Validar solo los 2 pasos necesarios
    return this.strategyService.validateStep(1) &&
           this.strategyService.validateStep(2);
  }

  next() {
    if (!this.canGoNext) {
      return;
    }
    this.strategyService.nextStep();
  }

  previous() {
    this.strategyService.previousStep();
  }

  save() {
    if (this.canSave) {
      this.onSave.emit();
    }
  }

  cancel() {
    this.onCancel.emit();
  }

  onStepClick(event: any) {
    // Permitir navegar a un paso anterior
    const index = typeof event === 'number' ? event : event.index;
    if (index < this.currentStep - 1) {
      this.strategyService.currentStep = index + 1;
    }
  }
}
